import { useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { Calendar, Loader2, Plus, X, ChevronRight, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { BackButton, SectionHeading, ToggleSwitch } from '@tendbot/ui'
import { defaultStagger } from '@tendbot/ui'
import { usePlugins, usePluginConnections } from '../plugins'
import { useDefaults } from '../settings'
import type { CalendarDefaultOption } from '../../shared/api/defaults'

interface Props {
  onBack: () => void
  onNavigate?: (screen: string) => void
}

export default function CalendarScreen({ onBack, onNavigate }: Props) {
  const { t } = useTranslation('settings')
  const { container, item } = defaultStagger

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4"
    >
      <div className="max-w-2xl mx-auto">
        <BackButton onClick={onBack} className="mb-2" />

        {/* Header */}
        <motion.div variants={item} className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
              <Calendar size={20} className="text-gold" />
            </div>
            <div>
              <h1 className="font-display text-2xl text-ivory font-semibold">{t('calendar.title')}</h1>
              <p className="text-pearl/60 text-xs">{t('calendar.subtitle')}</p>
            </div>
          </div>
        </motion.div>

        {/* Connected calendars */}
        <motion.div variants={item} className="mb-6">
          <SectionHeading>{t('calendar.connectedCalendars')}</SectionHeading>
          <ConnectedCalendars />
        </motion.div>

        {/* Briefing link */}
        <motion.div variants={item} className="mb-8">
          <SectionHeading>{t('calendar.relatedSettings')}</SectionHeading>
          <button
            onClick={() => onNavigate?.('briefing')}
            className="w-full glass-card rounded-xl p-4 flex items-center gap-3 hover:bg-slate-mid/30 transition-colors text-left"
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-mid/50">
              <Sun size={18} className="text-pearl/60" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-ivory text-sm font-medium block">{t('calendar.briefingLink')}</span>
              <span className="text-pearl/50 text-xs block">{t('calendar.briefingLinkDesc')}</span>
            </div>
            <ChevronRight size={16} className="text-pearl/55" />
          </button>
        </motion.div>
      </div>
    </motion.div>
  )
}

// ─── Connected calendars card ──────────────────────────────────

function ConnectedCalendars() {
  const { t } = useTranslation('settings')
  const { catalog, installed, installPlugin, refresh } = usePlugins()
  const {
    connections,
    connectingPlugin,
    handleConnectCalendar,
    handleConnect,
    handleDisconnect,
    handleToggleCalendarSync,
    handleToggleServiceSync,
  } = usePluginConnections({ catalog, installed, installPlugin, refresh })
  const { data: defaults, saving: savingDefault, error: defaultError, setDefaultCalendar } = useDefaults()

  const calendarConnections = useMemo(() => {
    return connections
      .filter(c => c.status === 'active')
      .filter(c => {
        if (c.provider === 'microsoft') return c.scopes?.some(s => s.includes('Calendars.Read'))
        if (c.provider === 'google') return c.scopes?.some(s => s.includes('calendar'))
        return false
      })
  }, [connections])

  const [removingId, setRemovingId] = useState<string | null>(null)
  const handleRemove = async (connectionId: string) => {
    setRemovingId(connectionId)
    try { await handleDisconnect(connectionId) } finally { setRemovingId(null) }
  }

  const hasGoogleWorkspace = catalog.some(p => p.id === 'google-workspace')
  const hasMicrosoft = catalog.some(p => p.id === 'microsoft-365')

  return (
    <div className="glass-card rounded-xl p-4 space-y-3">
      {/* Default calendar picker */}
      <DefaultCalendarPicker
        options={defaults?.calendarOptions ?? []}
        value={defaults?.defaultCalendarId ?? null}
        saving={savingDefault}
        error={defaultError}
        onChange={setDefaultCalendar}
      />

      {calendarConnections.length === 0 && (
        <p className="text-xs text-pearl/55">{t('calendar.noConnectedCalendars')}</p>
      )}

      {calendarConnections.map(conn => {
        const isRemoving = removingId === conn.connectionId
        const calendarEnabled = conn.calendarSyncEnabled !== false
        return (
          <div
            key={conn.connectionId}
            className={`p-3 rounded-xl bg-midnight/40 border border-white/5 ${isRemoving ? 'opacity-50' : ''}`}
          >
            {/* Account header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <ProviderIcon provider={conn.provider} />
                <div className="min-w-0">
                  <p className="text-sm text-ivory truncate">{conn.displayName || t('calendar.connected')}</p>
                  <p className="text-xs text-pearl/50">{providerLabel(conn.provider, t)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <ToggleSwitch
                  size="sm"
                  variant="bordered"
                  checked={calendarEnabled}
                  onChange={() => handleToggleServiceSync(conn.connectionId, { calendarSyncEnabled: !calendarEnabled })}
                />
                {isRemoving ? (
                  <Loader2 size={14} className="text-pearl/55 animate-spin" />
                ) : (
                  <button
                    onClick={() => handleRemove(conn.connectionId)}
                    disabled={!!removingId}
                    className="p-1 rounded hover:bg-rose/10 transition-colors disabled:opacity-50"
                    aria-label={t('calendar.disconnect')}
                  >
                    <X size={14} className="text-pearl/60 hover:text-rose" />
                  </button>
                )}
              </div>
            </div>

            {/* Per-calendar toggles */}
            {calendarEnabled && (conn.calendars?.length ?? 0) > 0 && (
              <div className="border-t border-white/5 pt-2 space-y-1">
                {conn.calendars!.map(cal => (
                  <div key={cal.id} className="flex items-center gap-2 text-xs py-1">
                    <Calendar size={10} className={cal.syncEnabled ? (cal.isPrimary ? 'text-gold/60' : 'text-pearl/55') : 'text-pearl/45'} />
                    <span className={`flex-1 truncate ${cal.syncEnabled ? 'text-pearl/80' : 'text-pearl/55'}`}>{cal.name}</span>
                    {cal.isPrimary && (
                      <span className="text-[10px] text-gold/50">{t('connection.primary')}</span>
                    )}
                    <ToggleSwitch
                      size="sm"
                      variant="bordered"
                      checked={cal.syncEnabled}
                      onChange={() => handleToggleCalendarSync(cal.id, !cal.syncEnabled)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}

      {/* Add buttons */}
      <div className="flex flex-wrap gap-2 pt-1">
        {hasGoogleWorkspace && (
          <button
            onClick={handleConnectCalendar}
            disabled={connectingPlugin === 'calendar'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/10 text-gold text-xs font-medium hover:bg-gold/20 transition-colors disabled:opacity-50"
          >
            {connectingPlugin === 'calendar' ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {t('calendar.addGoogle')}
          </button>
        )}
        {hasMicrosoft && (
          <button
            onClick={() => handleConnect('microsoft-365')}
            disabled={connectingPlugin === 'microsoft-365'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/10 text-gold text-xs font-medium hover:bg-gold/20 transition-colors disabled:opacity-50"
          >
            {connectingPlugin === 'microsoft-365' ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {t('calendar.addOutlook')}
          </button>
        )}
      </div>

      <p className="text-xs text-pearl/50 pt-1 leading-relaxed">
        {t('calendar.connectedCalendarsHint')}
      </p>
    </div>
  )
}

function ProviderIcon({ provider }: { provider: string }) {
  if (provider === 'microsoft') {
    return <div className="w-6 h-6 rounded bg-[#0078D4]/20 flex items-center justify-center shrink-0"><Calendar size={12} className="text-[#4aa3e5]" /></div>
  }
  return <div className="w-6 h-6 rounded bg-[#4285F4]/20 flex items-center justify-center shrink-0"><Calendar size={12} className="text-[#6ea3ff]" /></div>
}

function providerLabel(provider: string, t: (k: string) => string): string {
  if (provider === 'microsoft') return t('calendar.providerOutlook')
  if (provider === 'google') return t('calendar.providerGoogle')
  return provider
}

interface DefaultCalendarPickerProps {
  options: CalendarDefaultOption[]
  value: string | null
  saving: boolean
  error: string | null
  onChange: (calendarId: string | null) => Promise<void>
}

function DefaultCalendarPicker({ options, value, saving, error, onChange }: DefaultCalendarPickerProps) {
  const { t } = useTranslation('settings')

  // Group by account so users see "Account → Calendar" structure when a single
  // account exposes multiple calendars (e.g. shared family calendar).
  const grouped = useMemo(() => {
    const map = new Map<string, { accountDisplayName: string; provider: string; calendars: CalendarDefaultOption[] }>()
    for (const opt of options) {
      const existing = map.get(opt.connectionId)
      if (existing) {
        existing.calendars.push(opt)
      } else {
        map.set(opt.connectionId, {
          accountDisplayName: opt.accountDisplayName,
          provider: opt.provider,
          calendars: [opt],
        })
      }
    }
    return [...map.values()]
  }, [options])

  if (options.length === 0) return null

  // First writable calendar is the implicit default when nothing is set.
  const firstWritable = options.find(o => o.isWritable) ?? options[0]
  const effectiveValue = value ?? firstWritable?.calendarId ?? ''

  const showAccountPrefix = grouped.length > 1

  return (
    <div className="border-b border-white/5 pb-3 mb-1">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-pearl/70 font-medium">{t('calendar.defaultCalendar')}</span>
        {saving && <Loader2 size={12} className="text-pearl/55 animate-spin" />}
      </div>
      <select
        value={effectiveValue}
        disabled={saving}
        onChange={(e) => { void onChange(e.target.value) }}
        className="w-full text-sm bg-midnight/40 border border-white/10 rounded-lg px-3 py-2 text-ivory focus:outline-none focus:ring-1 focus:ring-gold/40 disabled:opacity-60"
      >
        {grouped.map(group => {
          const groupLabel = `${group.accountDisplayName} (${group.provider === 'microsoft' ? 'Outlook' : 'Google'})`
          const renderOption = (cal: CalendarDefaultOption) => {
            const tags: string[] = []
            if (cal.isPrimary) tags.push(t('calendar.primary'))
            if (!cal.isWritable) tags.push(t('calendar.readOnly'))
            const tagSuffix = tags.length > 0 ? ` — ${tags.join(', ')}` : ''
            const label = showAccountPrefix
              ? `${cal.accountDisplayName}: ${cal.name}${tagSuffix}`
              : `${cal.name}${tagSuffix}`
            return (
              <option key={cal.calendarId} value={cal.calendarId} disabled={!cal.isWritable}>
                {label}
              </option>
            )
          }
          return showAccountPrefix ? (
            <optgroup key={group.accountDisplayName} label={groupLabel}>
              {group.calendars.map(renderOption)}
            </optgroup>
          ) : (
            group.calendars.map(renderOption)
          )
        })}
      </select>
      <p className="text-xs text-pearl/50 mt-1.5 leading-relaxed">{t('calendar.defaultCalendarHint')}</p>
      {error && <p className="text-xs text-rose mt-1">{t('calendar.saveFailed')}</p>}
    </div>
  )
}
